
import * as React from 'react';
import {
	Table,
	Toolbar,
	Field,
	Button,
	CheckOutDoc2Icon,
	PlusFilledIcon,
	TrashcanIcon,
	PencilIcon,
	Loader,
	Alert
} from 'react-unity';
import DoNotDecryptRule from '../../../models/entities/DoNotDecryptRule';
import { RulesFormProps } from './dndrRule/AbstractRulesForm';
import RuleModalSubToInternet from './dndrRule/RuleModalSubToInternet';
import RuleModalSubToInternetAddNew from './dndrRule/RuleModalSubToInternetAddNew';
import RuleModalSubToOnPrem from './dndrRule/RuleModalSubToOnPrem';
import RuleModalSubToOnPremAddNew from './dndrRule/RuleModalSubToOnPremAddNew';
import RuleModalSubToSub from './dndrRule/RuleModalSubToSub';
import RuleModalSubToSubAddNew from './dndrRule/RuleModalSubToSubAddNew';
import { DoNotDecryptRequestsDirection } from '../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import CSVGeneratorButton from '../../common/CSVGeneratorButton';
import BulkUploadModal from './dndrRule/BulkUploadModal';
import Paginator from '../../common/tables/Paginator';
import { DNDRColumn, RuleColumnsLayout } from './RuleColumnsLayout';
import ImplementedRulesModal from './ImplementedRulesModal';
import { VirtualNetwork } from '../../../models/entities/AzureResources/VirtualNetwork';
import DNDROperationBadge from './DNDROperationBadge';
import TrashcanModal from './TrashcanModal';
import { RuleOperation } from '../../../models/enums/DNDR/RuleOperation';
import Select, { StylesConfig } from "react-select";
import './RulesTable.css';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import CompletedPORRules from '../../../models/entities/CompletedPORRules';

interface RulesTableProps {
	subscriptionRequestName?: string;
	rules: DoNotDecryptRule[];
	implementedRules?: DoNotDecryptRule[];
	validVirtualNetworks?: VirtualNetwork[];
	dndrDirection: DoNotDecryptRequestsDirection;
	readonly?: boolean;
	onChange: (rules: DoNotDecryptRule[]) => any;
	loadingImplementedRules?: boolean;
	failedImplementedRules?: boolean;
	selectedFromExisting?: string[];
	setSelectedFromExisting?: (rule: string[]) => any;
	fromDetails?: boolean;
	currentWorkflowState?: number;
	subscriptionRequestId?: number;
	completedPORRules?: CompletedPORRules;
}

interface RulesTableState {
	columns: DNDRColumn[];
	ruleModalVisible: boolean;
	editingRule?: DoNotDecryptRule;
	bulkUploadModalVisible: boolean;
	pageRows: DoNotDecryptRule[];
	implementedRuleModalVisible: boolean;
	optionValueSelected: string,
	showTrashcanModal: boolean;
}

export class RulesTable extends React.Component<RulesTableProps, RulesTableState> {

	constructor(props: RulesTableProps | Readonly<RulesTableProps>) {
		super(props);
		this.state = {
			columns: [],
			ruleModalVisible: false,
			bulkUploadModalVisible: false,
			pageRows: [],
			implementedRuleModalVisible: false,
			showTrashcanModal: false,
			optionValueSelected: "",
		};
	}

	componentDidMount() {
		this.setColumns();
	}

	componentDidUpdate(prevProps: RulesTableProps) {
		if (prevProps.dndrDirection?.value !== this.props.dndrDirection?.value) {
			this.setColumns();
		}
	}

	setColumns() {
		this.setState({
			columns: RuleColumnsLayout.get(this.props.dndrDirection)
		});
	}

	getRulesModalProps(): RulesFormProps {
		let editingRule = null;
		if (this.state.editingRule != null) {
			editingRule = { ...this.state.editingRule };
			if ((editingRule.isTemporary && editingRule.operation == RuleOperation.Modify)) editingRule.operation = RuleOperation.Remove;
			editingRule.name = editingRule.azureName ?? editingRule.name;
		}
		return {
			visible: this.state.ruleModalVisible,
			onConfirm: this.handleRuleChange,
			onClose: () => this.setState({ editingRule: null, ruleModalVisible: false }),
			editingRule: editingRule,
			validVirtualNetworks: this.props.validVirtualNetworks,
			completedPORRules: this.props.completedPORRules
		};
	}

	handleRuleChange = (rule: DoNotDecryptRule) => {
		let { rules } = this.props;

		if (this.state.editingRule) {
			const index = rules.indexOf(this.state.editingRule);
			rules = [...rules];
			rules[index] = rule;
		} else {
			rules = [...rules, rule];
		}
		this.props.onChange(rules);
		this.setState({ ruleModalVisible: false, editingRule: null });
	};

	handleMultipleRulesAdd = (rules: DoNotDecryptRule[]) => {
		this.props.onChange([...this.props.rules, ...rules]);
		this.setState({ ruleModalVisible: false, editingRule: null });
	};

	handleDeleteAll = () => {
		this.props.onChange([]);
		this.resetTrashcanModal();
	};

	handleDeleteOrRemoveRule = (ruleToRemove: DoNotDecryptRule) => {
		this.setState({ showTrashcanModal: true, editingRule: ruleToRemove })
	};

	handleRemoveRule = () => {
		const rules = this.props.rules.filter(rule => rule !== this.state.editingRule);
		this.props.setSelectedFromExisting(this.props.selectedFromExisting?.filter(ruleName => ruleName !== this.state.editingRule.name));
		this.props.onChange(rules);
		this.resetTrashcanModal();
	};

	handleDeleteRule = () => {
		let editingRule = { ...this.state.editingRule };
		editingRule.operation = RuleOperation.Remove;
		editingRule.name = editingRule.azureName;
		this.handleRuleChange(editingRule);
		this.resetTrashcanModal();
	};

	resetTrashcanModal = () => {
		this.setState({
			editingRule: null,
			showTrashcanModal: false
		})
	}

	closeTrashcanModal = this.resetTrashcanModal;

	handleBulkUpload = (rules: DoNotDecryptRule[]) => {
		this.setState({
			bulkUploadModalVisible: false,
			optionValueSelected: '',
		});
		this.props.onChange([...this.props.rules, ...rules]);
	};

	handlePageChange = (page: DoNotDecryptRule[]) => {
		this.setState({
			pageRows: page
		});
	};

	handleImplementedRulesOnSuccess = (impRules: DoNotDecryptRule[]) => {
		this.setState({
			implementedRuleModalVisible: false,
			optionValueSelected: '',
		});
		this.props.setSelectedFromExisting([...this.props.selectedFromExisting, ...impRules.map(rule => rule.name)])
		this.props.onChange([...this.props.rules, ...impRules]);
	};

	directionIs(direction: DoNotDecryptRequestsDirection) {
		return this.props.dndrDirection === direction;
	}

	getOperationName(rule: DoNotDecryptRule) {
		if (this.props.fromDetails && this.props.currentWorkflowState !== WorkflowState.Review.value) {
			return this.state.columns.find(column => column.header === 'Operation').cell(rule)
		}
		const nameColumn = this.state.columns.find(column => column.header === 'Name')
		const operationColumn = this.state.columns.find(column => column.header === 'Operation')
		return nameColumn.cell(rule) ? operationColumn.cell(rule) : 'to be updated'
	}

	handleOptionSelected = (option) => {
		this.setState({ optionValueSelected: option.value });
		if (option.value === "import-new-rules") {
			this.setState({ bulkUploadModalVisible: true });
		} else {
			if (!option.isDisabled) this.setState({ implementedRuleModalVisible: true });
		}
	}

	CustomOption = (props: { data: any; innerRef: any; innerProps: any; isDisabled: boolean }) => {
		const { data, innerRef, innerProps, isDisabled } = props;
		return (
			<div
				style={{ cursor: `${isDisabled ? 'not-allowed' : 'pointer'}` }}
				ref={innerRef}
				{...innerProps}
				className='custom-option-box'
			>
				<div style={{ color: `${isDisabled ? '#999' : '#1E55B1'}`, fontSize: 'large', }}>{data.isLoading ? data.loadingMessage : data.label}</div>
				<div style={{ color: '#999', fontSize: 'small', wordWrap: 'break-word', width: '180px' }}>{data.subtext}</div>
			</div>
		)
	}

	styles: StylesConfig<any, false> = {
		control: (css) => ({ ...css, width: 200, zIndex: 5 }),
	};

	render() {
		const noPORRules = this.props.completedPORRules && !this.props.completedPORRules.hasCompletedPORRules;

		const tableHeaders = this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column) => (
			<Table.Head.Cell key={column.header}>
				{column.header}
			</Table.Head.Cell>
		));
		const tableRows = [];
		const defaultLimit = 10;
		const options = [
			{
				value: "import-new-rules",
				label: 'Import new rules',
				subtext: 'Add new rules at the same time with CSV file',
				isDisabled: noPORRules || this.props.dndrDirection == null || !(this.props.validVirtualNetworks.length > 0),
				isLoading: this.props.dndrDirection == null || !(this.props.validVirtualNetworks.length > 0),
				loadingMessage: ''
			},
			{
				value: "import-existing-rule",
				label: 'Import existing rules',
				subtext: 'Add existing rules to delete or modify from this subscritpion',
				isDisabled: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				isLoading: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				loadingMessage: 'Loading Existing Rules...'
			},
		];

		this.state.pageRows.forEach((rule, rowIndex) => {
			tableRows.push(
				<Table.Body.Row key={rowIndex}>
					<Table.Body.Cell>
						{this.props.rules.indexOf(rule) + 1}
					</Table.Body.Cell>
					{this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column, colIndex) => (
						<Table.Body.Cell
							key={`${rowIndex}-${colIndex}`}
							className="em-u-margin-right-auto"
							title={(column.cell(rule) || '-')}
						>
							{column.header === 'Operation' ? (<DNDROperationBadge>{this.getOperationName(rule)}</DNDROperationBadge>) : (column.cell(rule)?.length > (column.limit || defaultLimit) ?
								(`${column.cell(rule).trim().substring(0, (column.limit || defaultLimit))}...`)
								:
								(column.cell(rule) || '-'))}
						</Table.Body.Cell>
					))}
					<Table.Body.Cell>
						{!this.props.readonly ?
							<Button.Group>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
								>
									<PencilIcon size="small" />
								</Button>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.handleDeleteOrRemoveRule(rule)}
								>
									<TrashcanIcon size="small" />
								</Button>
							</Button.Group>
							:
							<Button
								style={{ zIndex: 0 }}
								variant="primary"
								size="small"
								onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
							>
								See details
							</Button>
						}
					</Table.Body.Cell>
				</Table.Body.Row>
			);
		});

		return (
			<>
				{noPORRules && !this.props.readonly &&
					<Alert variant="warning" className="em-u-margin-bottom">
						No completed Port Opening Request (POR) rules found for this subscription.
						Please complete a POR before creating a Do Not Decrypt Request.
					</Alert>
				}
				<Field>
					<Field.Label>
						Ruleset
					</Field.Label>
					<Field.Body>
						<Table
							header={
								<Toolbar>
									{this.props.readonly &&
										<Toolbar.Item right>
											<CSVGeneratorButton
												data={this.props.rules}
												columns={this.state.columns}
											/>
										</Toolbar.Item>}
									{!this.props.readonly &&
										<Toolbar.Item right>
											<Field inline size="small">
												<Button.Group>
													<Button
														variant="primary"
														size="small"
														disabled={!this.props.dndrDirection || noPORRules}
														onClick={() => this.setState({ ruleModalVisible: true })}
													>
														{!(this.props.validVirtualNetworks.length > 0) ?
															<>
																{(this.props.failedImplementedRules) ?
																	<>
																		<Loader className="icon-loader em-c-icon--small" />
																		<span>Operation Failed</span>
																	</>
																	:
																	<>
																		<Loader className="icon-loader em-c-icon--small" />
																		<span>Loading Virtual Networks...</span>
																	</>}
															</>
															:
															noPORRules ?
																<>
																	<span>No Completed POR</span>
																</>
																:
																<>
																	<PlusFilledIcon size="small" />
																	<span>Add New Rule</span>
																</>}
													</Button>
													<Select
														placeholder='Import rules'
														value={this.state.optionValueSelected != null ? this.state.optionValueSelected : ''}
														onChange={(option) => this.handleOptionSelected(option)}
														components={{ Option: this.CustomOption }}
														options={options}
														styles={this.styles}
													/>
												</Button.Group>
											</Field>
										</Toolbar.Item>}
								</Toolbar>
							}
							footer={
								<Paginator
									data={this.props.rules}
									onPageChange={this.handlePageChange}
								/>
							}
						>
							<Table.Head>
								<Table.Head.Row>
									<Table.Head.Cell />
									{tableHeaders}
									<Table.Head.Cell>
										{!this.props.readonly ? 'Actions' : ''}
									</Table.Head.Cell>
								</Table.Head.Row>
							</Table.Head>
							<Table.Body>
								{tableRows.length > 0 ?
									tableRows
									:
									<Table.Body.Row>
										<Table.Body.Cell
											colSpan={this.state.columns?.length + 2 || 2}
										>
											<i>No rules found.</i>
										</Table.Body.Cell>
									</Table.Body.Row>}
							</Table.Body>
						</Table>
					</Field.Body>
				</Field>
				{this.state.showTrashcanModal &&
					<TrashcanModal
						onClose={this.resetTrashcanModal}
						onDelete={this.handleDeleteRule}
						onRemove={this.handleRemoveRule}
					/>
				}

				{/* SubscriptionToInternet: AddNew modal for new rules, original modal for editing */}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToInternet) &&
					<RuleModalSubToInternetAddNew
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule == null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
						onConfirmMultiple={this.handleMultipleRulesAdd}
					/>}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToInternet) &&
					<RuleModalSubToInternet
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule != null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
					/>}

				{/* SubscriptionToOnPrem: AddNew modal for new rules, original modal for editing */}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToOnPrem) &&
					<RuleModalSubToOnPremAddNew
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule == null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
						onConfirmMultiple={this.handleMultipleRulesAdd}
					/>}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToOnPrem) &&
					<RuleModalSubToOnPrem
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule != null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
					/>}

				{/* SubscriptionToSubscription: AddNew modal for new rules, original modal for editing */}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToSubscription) &&
					<RuleModalSubToSubAddNew
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule == null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
						onConfirmMultiple={this.handleMultipleRulesAdd}
					/>}
				{this.directionIs(DoNotDecryptRequestsDirection.SubscriptionToSubscription) &&
					<RuleModalSubToSub
						{...this.getRulesModalProps()}
						visible={this.state.ruleModalVisible && this.state.editingRule != null}
						subscriptionRequestName={this.props.subscriptionRequestName}
						isReadOnly={this.props.readonly}
					/>}

				{this.state.implementedRuleModalVisible &&
					<ImplementedRulesModal
						direction={this.props.dndrDirection}
						rules={this.props.implementedRules}
						onSuccess={this.handleImplementedRulesOnSuccess}
						visible={this.state.implementedRuleModalVisible}
						alreadySelected={this.props.selectedFromExisting}
						onCancel={(ev) => {
							this.setState({
								implementedRuleModalVisible: false,
								optionValueSelected: '',
							});
						}}
					/>}
				<BulkUploadModal
					subscriptionRequestId={this.props.subscriptionRequestId}
					visible={this.state.bulkUploadModalVisible}
					direction={this.props.dndrDirection}
					onSuccess={this.handleBulkUpload}
					onCancel={(ev) => {
						this.setState({
							bulkUploadModalVisible: false,
							optionValueSelected: '',
						});
					}}
				/>
			</>
		);
	}
}